import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiErrorMessage } from "./helpers/apiErrorMessage";

function Settings() {
    const [EImode, setEImode] = useState('Export JSON');
    const [selectedList, setSelectedList] = useState('Diary');
    const [file, setFile] = useState(null); // FileStorage for import file upload
    const [message, setMessage] = useState('');
    const [isMessageError, setIsMessageError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [diarySettings, setDiarySettings] = useState({ enableRatings: true, showCoverArt: true });
    const [diaryMessage, setDiaryMessage] = useState('');
    const [isDiaryError, setIsDiaryError] = useState(false);
    const [visibilitySettings, setVisibilitySettings] = useState({ isDiaryPublic: true, isWishlistPublic: true, isFriendsPublic: true, isStatisticsPublic: true });
    const [visibilityMessage, setVisibilityMessage] = useState('');
    const [isVisibilityError, setIsVisibilityError] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordMessage, setPasswordMessage] = useState('');
    const [isPasswordError, setIsPasswordError] = useState(false);

    useEffect(() => {
        fetchDiarySettings();
        fetchVisibilitySettings();
        setLoading(false);
    }, []);

    const fetchDiarySettings = async () => {
        try {
            const response = await axios.get('/api/settings/diary');
            setDiarySettings(response.data);
        } catch (error) {
            setDiaryMessage(getApiErrorMessage(error, 'load diary settings'));
            setIsDiaryError(true);
            console.error('Error fetching diary settings:', error);
        }
    };

    const fetchVisibilitySettings = async () => {
        try {
            const response = await axios.get('/api/settings/visibility');
            setVisibilitySettings(response.data);
        } catch (error) {
            setVisibilityMessage(getApiErrorMessage(error, 'load visibility settings'));
            setIsVisibilityError(true);
            console.error('Error fetching visibility settings:', error);
        }
    };

    const handleSettingToggle = async (settings, title, field) => {
        const updatedSettings = {
            ...settings,
            [field]: !settings[field]
        };

        try {
            const response = await axios.post(`/api/settings/${title}`, updatedSettings);
            if (title === 'diary') {
                setDiarySettings(updatedSettings);
                setDiaryMessage(response.data.message);
                setIsDiaryError(false);
            } else if (title === 'visibility') {
                setVisibilitySettings(updatedSettings);
                setVisibilityMessage(response.data.message);
                setIsVisibilityError(false);
            }
        } catch (error) {
            if (title === 'diary') {
                setDiaryMessage(error.response?.data?.message || 'Error saving diary settings');
                setIsDiaryError(true);
            } else if (title === 'visibility') {
                setVisibilityMessage(error.response?.data?.message || 'Error saving visibility settings');
                setIsVisibilityError(true);
            }
        }
    };

    const handleDiarySettingToggle = async (field) => { handleSettingToggle(diarySettings, 'diary', field); };
    const handleVisibilitySettingToggle = async (field) => { handleSettingToggle(visibilitySettings, 'visibility', field); };

    const handleExportImport = async () => {
        try {
            if (EImode === 'Export JSON') {
                fetch(`/api/settings/export?list=${selectedList}`)
                    .then(res => res.json())
                    .then(data => exportToJsonFile(data));
            } else {
                if (!file) { alert('Please select a file to import'); return; }

                const formData = new FormData();
                formData.append("file", file);
                formData.append("selectedList", selectedList);

                const response = await axios.post(`api/settings/import`, formData, {
                      headers: { "Content-Type": "multipart/form-data", },
                });

                setMessage(response.data.message);
                setIsMessageError(false);
            }
        } catch (error) {
            console.error("Export/Import operation failed:", error);
            setMessage(error.response?.data?.message || 'Export/Import operation failed');
            setIsMessageError(true);
        }
    };

    function exportToJsonFile(data) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selectedList}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handlePasswordChange = (e) => {
        setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    };

    const handleChangePassword = async () => {
        if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            setPasswordMessage('All fields are required');
            setIsPasswordError(true);
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setPasswordMessage('New passwords do not match');
            setIsPasswordError(true);
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            setPasswordMessage('New password must be at least 6 characters');
            setIsPasswordError(true);
            return;
        }

        try {
            const res = await axios.post('/api/auth/change-password', {
                oldPassword: passwordForm.oldPassword,
                newPassword: passwordForm.newPassword
            });
            setPasswordMessage(res.data.message);
            setIsPasswordError(false);
            setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setPasswordMessage(error.response?.data?.message || 'Error changing password');
            setIsPasswordError(true);
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white px-6 pb-10">
            <section className="mb-10">
                <h2 className="text-xl font-semibold uppercase text-sm mb-4 text-gray-300">Security</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-gray-400 text-sm mb-1">Current Password</label>
                        <input
                            type="password"
                            name="oldPassword"
                            placeholder="Enter current password"
                            value={passwordForm.oldPassword}
                            onChange={handlePasswordChange}
                            className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm mb-1">New Password</label>
                        <input
                            type="password"
                            name="newPassword"
                            placeholder="Enter new password"
                            value={passwordForm.newPassword}
                            onChange={handlePasswordChange}
                            className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm mb-1">Confirm Password</label>
                        <input
                            type="password"
                            name="confirmPassword"
                            placeholder="Confirm new password"
                            value={passwordForm.confirmPassword}
                            onChange={handlePasswordChange}
                            className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                    </div>
                    <button
                        onClick={handleChangePassword}
                        className="bg-green-500 text-black font-semibold px-4 py-2 rounded hover:bg-green-400 transition"
                    >
                        Change Password
                    </button>
                    {passwordMessage && (
                        <p className={`text-sm ${isPasswordError ? 'text-red-400' : 'text-green-400'}`}>{passwordMessage}</p>
                    )}
                </div>
            </section>

            <section className="mb-10">
                <h2 className="text-xl uppercase font-semibold mb-4 text-gray-300">Diary Settings</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Enable album ratings</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={diarySettings.enableRatings}
                            onChange={() => handleDiarySettingToggle('enableRatings')}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Show cover art</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={diarySettings.showCoverArt}
                            onChange={() => handleDiarySettingToggle('showCoverArt')}
                        />
                    </div>
                    {diaryMessage && (
                        <p className={`text-sm ${isDiaryError ? 'text-red-400' : 'text-green-400'}`}>{diaryMessage}</p>
                    )}
                </div>
            </section>

            <section className="mb-10">
                <h2 className="text-xl uppercase font-semibold mb-4 text-gray-300">Visibility Settings</h2>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Hide Diary</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={!visibilitySettings.isDiaryPublic}
                            onChange={() => handleVisibilitySettingToggle('isDiaryPublic')}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Hide Wishlist</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={!visibilitySettings.isWishlistPublic}
                            onChange={() => handleVisibilitySettingToggle('isWishlistPublic')}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Hide Friends</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={!visibilitySettings.isFriendsPublic}
                            onChange={() => handleVisibilitySettingToggle('isFriendsPublic')}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-400">Hide Statistics</span>
                        <input
                            type="checkbox"
                            className="accent-green-500 h-5 w-5"
                            checked={!visibilitySettings.isStatisticsPublic}
                            onChange={() => handleVisibilitySettingToggle('isStatisticsPublic')}
                        />
                    </div>
                    {visibilityMessage && (
                        <p className={`text-sm ${isVisibilityError ? 'text-red-400' : 'text-green-400'}`}>{visibilityMessage}</p>
                    )}
                </div>
            </section>

            <section className="mb-10">
                <h2 className="text-xl uppercase font-semibold mb-4 text-gray-300">Theme</h2>
                <div className="flex items-center gap-4">
                    <button
                        className="bg-green-500 text-black font-semibold px-4 py-2 rounded hover:bg-green-400 transition">
                        Dark Mode
                    </button>
                    <button
                        className="bg-gray-700 text-white font-semibold px-4 py-2 rounded hover:bg-gray-600 transition">
                        Light Mode
                    </button>
                </div>
            </section>

            <section className="mb-10">
                <h2 className="text-xl uppercase font-semibold mb-4 text-gray-300">IMPORT / EXPORT</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-gray-400 text-sm mb-1">Select List</label>
                        <select
                            value={selectedList}
                            onChange={(e) => setSelectedList(e.target.value)}
                            className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                            <option>Diary</option>
                            <option>Wishlist</option>
                        </select>
                        <label className="block text-gray-400 text-sm mb-1 ml-5">
                            Note: to import / export any of your lists use the corresponding button on the corresponding
                            list page.
                        </label>
                    </div>
                    <div>
                        <label className="block text-gray-400 text-sm mb-1">Mode</label>
                        <select
                            value={EImode}
                            onChange={(e) => setEImode(e.target.value)}
                            className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                            <option>Export JSON</option>
                            <option>Import JSON</option>
                        </select>
                    </div>
                    {EImode === 'Import JSON' && (
                        <div>
                            <label className="block text-gray-400 text-sm mb-1">Select File</label>
                            <input
                                type="file"
                                accept=".json"
                                onChange={handleFileChange}
                                className="w-full bg-[#1a1c28] text-white rounded px-4 py-2 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                        </div>
                    )}
                    <div className="flex gap-4">
                        <button
                            onClick={handleExportImport}
                            className="bg-green-500 text-black font-semibold px-4 py-2 rounded hover:bg-green-400 transition">
                            {EImode === 'Export JSON' ? 'Export' : 'Import'}
                        </button>
                    </div>
                    {message && <p className={`text-sm ${isMessageError ? 'text-red-400' : 'text-green-400'}`}>{message}</p>}
                </div>
            </section>
        </div>
    )
}

export default Settings;